<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>{{ $t('reservations.subtitle') }}</span>
      </div>
      <div class="ov-header__actions">
        <button type="button" class="ov-btn ov-btn--primary" @click="$router.push('/selectionnerOffreVoyageReservation')">
          <v-icon small>mdi-plus</v-icon>
          Nouvelle réservation
        </button>
      </div>
    </div>

    <div class="ov-grid">
      <!-- ===== RÉSERVATIONS ===== -->
      <div class="ov-card">
        <div class="ov-card__head">
          <div class="ov-card__title">
            Réservations
            <span class="ov-count">{{ reservationTicketList.length }}</span>
          </div>
          <div class="ov-search">
            <v-icon small>mdi-magnify</v-icon>
            <input v-model="search" type="text" class="ov-input" :placeholder="$t('reservations.searchPlaceholder')" />
          </div>
        </div>

        <v-data-table
          class="ov-table"
          :headers="headers"
          :search="search"
          :loading="loading"
          loader-height="2"
          :items="reservationTicketList"
          :items-per-page="10"
          :loading-text="$t('reservations.loadingReservations')"
          :no-data-text="$t('reservations.noReservationAvailable')"
        >
          <template v-slot:[`item.designation`]="{ item }">
            <span class="srb-ref">{{ item.designation }}</span>
          </template>

          <template v-slot:[`item.villeDepartDesignation`]="{ item }">
            <div class="srb-route">
              <span>{{ getDepart(item) }}</span>
              <span class="srb-route__line"></span>
              <span>{{ getDestination(item) }}</span>
            </div>
          </template>

          <template v-slot:[`item.clientDetails.nom`]="{ item }">
            <div class="srb-client">
              {{ item.clientDetails ? [item.clientDetails.nom, item.clientDetails.prenoms].filter(Boolean).join(' ') : '—' }}
            </div>
          </template>

          <template v-slot:[`item.nombrePlace`]="{ item }">
            <span class="ov-badge ov-badge--navy ov-badge--plain">{{ item.nombrePlace }}</span>
          </template>

          <template v-slot:[`item.montantTotalReservation`]="{ item }">
            <span class="srb-amount">{{ formatAmount(item.montantTotalReservation) }} <span>FCFA</span></span>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="ov-row-actions srb-actions">
              <button type="button" class="ov-icon-btn" :title="$t('reservations.details')" @click="checkBooking(item)">
                <v-icon small>mdi-eye-outline</v-icon>
              </button>
              <button type="button" class="ov-icon-btn" :title="$t('reservations.print')" @click="printBookingTicket(item.designation)">
                <v-icon small>mdi-printer-outline</v-icon>
              </button>
            </div>
          </template>
        </v-data-table>
      </div>

      <!-- ===== RÉCAPITULATIF ===== -->
      <div class="ov-sticky">
        <div class="ov-card">
          <div class="ov-card__head">
            <div class="ov-card__title">Récapitulatif</div>
          </div>
          <div class="ov-card__body">
            <div class="ov-stat">
              <span class="ov-stat__label">{{ $t('reservations.totalReservations') }}</span>
              <span class="ov-stat__value">{{ reservationTicketList.length }}</span>
            </div>
            <div class="ov-stat">
              <span class="ov-stat__label">{{ $t('reservations.reservedSeats') }}</span>
              <span class="ov-stat__value">{{ totalPlaces }}</span>
            </div>
            <div class="ov-stat">
              <span class="ov-stat__label">{{ $t('reservations.totalAmount') }}</span>
              <span class="ov-stat__value ov-stat__value--accent">{{ formatAmount(totalPriceReservation) }} FCFA</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-overlay :value="overlay"><v-progress-circular indeterminate size="56" color="#159e72"></v-progress-circular></v-overlay>
  </div>
</template>

<script>
import { reservationService } from '@/services';
import { mapGetters, mapActions } from 'vuex';

export default {
  name: "SelectionnerReservationBillet",
  
  data() {
    return {
      headers: [
        { text: 'Référence', value: "designation", align: "start" },
        { text: this.$t('reservations.route'), value: "villeDepartDesignation", align: "start" },
        { text: 'Client', value: "clientDetails.nom", align: "start" },
        { text: this.$t('reservations.seats'), value: "nombrePlace", align: "center" },
        { text: this.$t('reservations.amount'), value: "montantTotalReservation", align: "end" },
        { text: this.$t('reservations.actions'), value: "actions", sortable: false, align: "end" },
        // Colonnes masquées : gardées pour que la recherche porte aussi sur ces champs
        { text: '', value: "clientDetails.prenoms", align: " d-none" },
        { text: '', value: "villeDestinationDesignation", align: " d-none" },
        { text: '', value: "offreVoyageDesignation", align: " d-none" },
      ],
      search: "",
      successMsg: "",
      errorMsg: "",
    };
  },

  computed: {
    ...mapGetters('reservation', {
      reservationTicketList: 'allReservations',
      loading: 'isLoading',
    }),

    totalPriceReservation() {
      return this.reservationTicketList.reduce((total, reservation) => {
        return total + (reservation.montantTotalReservation || 0);
      }, 0);
    },
    
    totalPlaces() {
      return this.reservationTicketList.reduce((total, reservation) => {
        return total + (reservation.nombrePlace || 0);
      }, 0);
    },

    overlay() {
      return this.loading;
    },
  },

  methods: {
    ...mapActions('reservation', {
      fetchReservationsBySeller: 'fetchReservationsBySeller',
      fetchReservationsByAdminTP: 'fetchReservationsByAdminTP',
    }),

    // Convert File into Base 64
    convertToBase64(byte) {
      if (byte != null && byte != "") {
        window.open(
          "data:application/pdf;base64," + byte,
          "_blank",
          "fullscreen=yes"
        );
      }
    },

    // Générer le billet de voyage
    async printBookingTicket(designationBillet) {
      if (!designationBillet) {
        this.$swal.fire(
          this.$t('reservations.ticketReferenceNotFound'),
          this.$t('reservations.cannotPrint'),
          "error"
        );
        return;
      }

      try {
        const blob = await reservationService.generateTicket(designationBillet);
        const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `billet-${designationBillet}.pdf`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 15000);
        this.$handleSuccess(this.$t('reservations.ticketGenerated'));
      } catch (error) {
        this.$handleError(error, 'Génération du billet');
      }
    },
    // Obtenir la liste des réservations
    async getAllReservationTicketAvailable() {
      try {
        const userRole = this.$store.state.userAuthentified.roleCode;
        const gareDesignation = this.$store.state.userAuthentified.gareDesignation;
        
        if (userRole === "RoleAdminCompagnieTransport") {
          await this.fetchReservationsByAdminTP({});
        } else {
          // Pour les vendeurs/agents de gare, envoyer UNIQUEMENT la gare
          // Créer un nouvel objet pour éviter d'envoyer d'autres champs
          const dataToSend = { gareDesignation: gareDesignation };
          await this.fetchReservationsBySeller(dataToSend);
        }
        // Notification de succès uniquement si des réservations sont chargées
        if (this.reservationTicketList.length > 0) {
          this.$handleSuccess(this.$t('reservations.reservationsLoaded'));
        } else {
          console.warn('⚠️ Aucune réservation trouvée');
        }
      } catch (error) {
        console.error('❌ Erreur chargement réservations:', error);
        this.$handleError(error, 'Chargement des réservations');
      }
    },

    formatAmount(value) {
      return Number(value || 0).toLocaleString('fr-FR');
    },

    getDepart(item) {
      if (item.villeDepartDesignation) return item.villeDepartDesignation;
      if (item.offreVoyageDesignation) {
        // Séparer par -, –, → ou ->
        const parts = item.offreVoyageDesignation.split(/\s*[-–→>]+\s*/);
        return parts[0]?.trim() || '—';
      }
      return '—';
    },

    getDestination(item) {
      if (item.villeDestinationDesignation) return item.villeDestinationDesignation;
      if (item.offreVoyageDesignation) {
        // Séparer par -, –, → ou ->
        const parts = item.offreVoyageDesignation.split(/\s*[-–→>]+\s*/);
        return parts[1]?.trim() || '—';
      }
      return '—';
    },

    // Consulter une réservation
    checkBooking(reservation) {
      // Enrichir la réservation avec les villes de départ et destination
      const enrichedReservation = {
        ...reservation,
        villeDepartDesignation: this.getDepart(reservation),
        villeDestinationDesignation: this.getDestination(reservation),
      };
      
      this.$store.commit('STORE_RESERVATION_BILLET', enrichedReservation);
      this.$router.push({ path: "/DetailTicketReservation" });
    },
  },

  mounted() {
    this.getAllReservationTicketAvailable();
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/admin/ui";

.srb-ref {
  font-weight: 800;
  color: $ov-navy;
  text-transform: uppercase;
}

.srb-route {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: $ov-ink;
  white-space: nowrap;
}

.srb-route__line {
  width: 22px;
  border-top: 2px dashed rgba(21, 158, 114, 0.55);
}

.srb-client {
  font-weight: 600;
  color: $ov-ink;
}

.srb-amount {
  font-weight: 800;
  color: $ov-navy;
  white-space: nowrap;

  span {
    font-size: 11px;
    font-weight: 700;
    color: $ov-subtle;
  }
}

.srb-actions {
  justify-content: flex-end;
}
</style>
